import { ALL_RISK_LEVEL_VALUE, DEFAULT_SIMULATION_SORT, MAX_CARD_PARTICIPANTS } from './constants'

export const SIMULATIONS_FALLBACK_PAGE = 1

export const isSimulationPageGone = (error) => error?.status === 404

// High behavioral risk is a boolean today (no Medium/Low). Within High:
// Serial Clicker → Untrained. Everyone else shares one lower priority band.
const getBehavioralRiskSortRank = ({ isHighBehavioralRisk, isSerialClicker, isUntrained }) => {
  const isHigh = Boolean(isHighBehavioralRisk || isSerialClicker || isUntrained)

  if (!isHigh) {
    return 2
  }

  if (isSerialClicker) {
    return 0
  }

  return 1
}

const getLastSimulatedSortValue = (lastSimulationTime) => {
  if (!lastSimulationTime) {
    return Number.NEGATIVE_INFINITY
  }

  const timestamp = new Date(lastSimulationTime).getTime()

  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp
}

export const sortSuggestedParticipants = (participants = []) =>
  [...participants].sort((left, right) => {
    const riskDiff = getBehavioralRiskSortRank(left) - getBehavioralRiskSortRank(right)

    if (riskDiff !== 0) {
      return riskDiff
    }

    const lastSimulatedDiff =
      getLastSimulatedSortValue(left.lastSimulationTime) -
      getLastSimulatedSortValue(right.lastSimulationTime)

    if (lastSimulatedDiff !== 0) {
      return lastSimulatedDiff
    }

    return (left.name || '').localeCompare(right.name || '', undefined, {
      sensitivity: 'base',
    })
  })

export const capSuggestedParticipants = (participants = []) =>
  sortSuggestedParticipants(participants).slice(0, MAX_CARD_PARTICIPANTS)

export const getOwnedParticipantsCount = (campaign, excludedParticipants = {}) => {
  if (!campaign) return 0

  const excludedCount = excludedParticipants[campaign.id]?.length || 0

  return campaign.audience.suggestedProfiles.length - excludedCount
}

const SIMULATION_LIST_ORDERING_BY_COLUMN = {
  createdAt: 'created_at',
  lastEngagementAt: 'last_engagement_at',
  participantsCount: 'participants_count',
  clickRate: 'click_rate',
  reportRate: 'report_rate',
  title: 'title',
}

// psima DATE_TIME_FORMAT is `%Y-%m-%dT%H:%M:%SZ` and rejects milliseconds.
const formatUtcDateTime = (date) => date.toISOString().replace(/\.\d{3}Z$/, 'Z')

const buildSimulationOrdering = (sort) => {
  const isDefaultSort =
    sort.key === DEFAULT_SIMULATION_SORT.key && sort.direction === DEFAULT_SIMULATION_SORT.direction
  if (isDefaultSort) return undefined

  const field = SIMULATION_LIST_ORDERING_BY_COLUMN[sort.key]
  return sort.direction === 'desc' ? `-${field}` : field
}

export const buildLaunchedAtBounds = (dateRange) => {
  if (!dateRange) return {}

  const start = new Date(dateRange.startDate)
  start.setHours(0, 0, 0, 0)
  const end = new Date(dateRange.endDate)
  end.setHours(0, 0, 0, 0)
  end.setDate(end.getDate() + 1)

  return {
    launchedAfter: formatUtcDateTime(start),
    launchedBefore: formatUtcDateTime(end),
  }
}

const isSameDay = (first, second) =>
  new Date(first).toDateString() === new Date(second).toDateString()

export const isSameDateRange = (first, second) => {
  if (!first || !second) return !first && !second
  return isSameDay(first.startDate, second.startDate) && isSameDay(first.endDate, second.endDate)
}

export const isSameSort = (first, second) =>
  first?.key === second?.key && first?.direction === second?.direction

export const buildSimulationFilters = ({
  statusSelectedFilters,
  selectedDepartments,
  dateRange,
}) => {
  const filters = buildLaunchedAtBounds(dateRange)
  if (statusSelectedFilters.length && !statusSelectedFilters.includes(ALL_RISK_LEVEL_VALUE)) {
    filters.status = statusSelectedFilters
  }
  if (selectedDepartments.length) filters.department = selectedDepartments
  return filters
}

export const buildSimulationListQuery = (simulationData) => {
  const filters = buildSimulationFilters(simulationData)
  const ordering = buildSimulationOrdering(simulationData.sort)
  return ordering ? { ...filters, ordering } : filters
}

export const isCampaignInactive = (campaign, excludedParticipants = {}) =>
  getOwnedParticipantsCount(campaign, excludedParticipants) === 0

export const getActiveSelectedCampaignIds = (
  items = [],
  selectedCampaigns = [],
  excludedParticipants = {}
) =>
  selectedCampaigns.filter((campaignId) => {
    const campaign = items.find(({ id }) => id === campaignId)

    return !!campaign && getOwnedParticipantsCount(campaign, excludedParticipants) > 0
  })
